// src/stores/sessionStore.jsx
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

const useSessionStore = create(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Session state
      currentSession: null,
      sessionStatus: 'idle', // idle, starting, live, ending, ended
      sessionSettings: {
        disableStudentVideo: false,
        disableStudentAudio: false,
        recordingEnabled: true,
        virtualRoomsEnabled: false,
        autoAssignBreakoutRooms: false,
      },

      // Participants
      participants: [],
      localParticipant: null,

      // Virtual Rooms (replacing breakout rooms)
      virtualRooms: [],
      currentVirtualRoom: undefined, // FIX: Changed from null to undefined to avoid race conditions
      participantVirtualRooms: {}, // { participantId: virtualRoomId }
      teacherBroadcastRooms: new Set(), // Set of room IDs teacher is broadcasting to

      // Recording
      isRecording: false,
      recordings: [],

      // Connection
      connectionQuality: 'excellent',
      isConnected: false,
      isConnectionEstablishing: true,

      // Actions
      setCurrentSession: (session) => set({ currentSession: session }),
      setSessionStatus: (status) => set({ sessionStatus: status }),
      updateSessionSettings: (settings) => set((state) => ({
        sessionSettings: { ...state.sessionSettings, ...settings }
      })),

      setParticipants: (participants) => set({ participants }),
      addParticipant: (participant) => set((state) => ({
        participants: [...state.participants, participant]
      })),
      removeParticipant: (participantId) => set((state) => ({
        participants: state.participants.filter(p => p.id !== participantId)
      })),
      updateParticipant: (participantId, updates) => set((state) => ({
        participants: state.participants.map(p => 
          p.id === participantId ? { ...p, ...updates } : p
        )
      })),

      // Virtual Rooms Actions
      setVirtualRooms: (rooms) => set({ virtualRooms: rooms }),
      addVirtualRoom: (room) => set((state) => ({
        virtualRooms: [...state.virtualRooms, room]
      })),
      updateVirtualRoom: (roomId, updates) => set((state) => ({
        virtualRooms: state.virtualRooms.map(r => 
          r.id === roomId ? { ...r, ...updates } : r
        )
      })),
      removeVirtualRoom: (roomId) => set((state) => ({
        virtualRooms: state.virtualRooms.filter(r => r.id !== roomId)
      })),

      setCurrentVirtualRoom: (room) => set({ currentVirtualRoom: room }),

      // Participant Virtual Room Assignment
      assignParticipantToVirtualRoom: (participantId, virtualRoomId) => set((state) => ({
        participantVirtualRooms: {
          ...state.participantVirtualRooms,
          [participantId]: virtualRoomId
        }
      })),

      removeParticipantFromVirtualRoom: (participantId) => set((state) => {
        const newAssignments = { ...state.participantVirtualRooms };
        delete newAssignments[participantId];
        return { participantVirtualRooms: newAssignments };
      }),

      // Teacher Broadcast Management
      setTeacherBroadcastRooms: (roomIds) => set({ 
        teacherBroadcastRooms: new Set(roomIds) 
      }),
      
      addTeacherBroadcastRoom: (roomId) => set((state) => {
        const newSet = new Set(state.teacherBroadcastRooms);
        newSet.add(roomId);
        return { teacherBroadcastRooms: newSet };
      }),
      
      removeTeacherBroadcastRoom: (roomId) => set((state) => {
        const newSet = new Set(state.teacherBroadcastRooms);
        newSet.delete(roomId);
        return { teacherBroadcastRooms: newSet };
      }),
      
      toggleTeacherBroadcastRoom: (roomId) => set((state) => {
        const newSet = new Set(state.teacherBroadcastRooms);
        if (newSet.has(roomId)) {
          newSet.delete(roomId);
        } else {
          newSet.add(roomId);
        }
        return { teacherBroadcastRooms: newSet };
      }),

      setRecordingStatus: (isRecording) => set({ isRecording }),
      setRecordings: (recordings) => set({ recordings }),

      setConnectionQuality: (quality) => set({ connectionQuality: quality }),
      setIsConnected: (isConnected) => set({ isConnected }),
      setIsConnectionEstablishing: (isEstablishing) => set({ isConnectionEstablishing: isEstablishing }),

      // Complex actions
      moveParticipantToVirtualRoom: (participantId, virtualRoomId) => {
        const state = get();
        const participant = state.participants.find(p => p.id === participantId);
        if (!participant) return;

        // Update participant's virtual room assignment
        if (virtualRoomId) {
          set((state) => ({
            participantVirtualRooms: {
              ...state.participantVirtualRooms,
              [participantId]: virtualRoomId
            },
            participants: state.participants.map(p => 
              p.id === participantId 
                ? { ...p, virtualRoomId: virtualRoomId } 
                : p
            )
          }));
        } else {
          // Remove from virtual room (return to main)
          set((state) => {
            const newAssignments = { ...state.participantVirtualRooms };
            delete newAssignments[participantId];
            return {
              participantVirtualRooms: newAssignments,
              participants: state.participants.map(p => 
                p.id === participantId 
                  ? { ...p, virtualRoomId: null } 
                  : p
              )
            };
          });
        }
      },

      // Clear all virtual rooms
      clearAllVirtualRooms: () => set({
        virtualRooms: [],
        currentVirtualRoom: undefined, // FIX: Set to undefined instead of null
        participantVirtualRooms: {},
        teacherBroadcastRooms: new Set(),
        participants: get().participants.map(p => ({ ...p, virtualRoomId: null }))
      }),

      // FIX: Reset function with proper initial values
      reset: () => set({
        currentSession: null,
        sessionStatus: 'idle',
        sessionSettings: {
          disableStudentVideo: false,
          disableStudentAudio: false,
          recordingEnabled: true,
          virtualRoomsEnabled: false,
        },
        participants: [],
        localParticipant: null,
        virtualRooms: [],
        currentVirtualRoom: undefined, // FIX: Set to undefined instead of null
        participantVirtualRooms: {},
        teacherBroadcastRooms: new Set(),
        isRecording: false,
        recordings: [],
        connectionQuality: 'excellent',
        isConnected: false,
        isConnectionEstablishing: true,
      }),

      // FIX: Add a method to safely reset session store without connection issues
      resetSessionStore: () => {
        const isConnected = get().isConnected;
        if (!isConnected) {
          get().reset();
        } else {
          console.warn('Cannot reset store while connected to session');
        }
      }
    })),
    {
      name: 'session-store',
    }
  )
);

export default useSessionStore;